import type { ReactNode } from "react";
import {
  CalendarDays,
  CheckCircle2,
  Clock3,
  ExternalLink,
  FileText,
  Gift,
  ImageOff,
  MapPin,
  ScanLine,
  Tag,
} from "lucide-react";
import { Button, Card, CardContent } from "../../../components";
import { VenueSize, VenueSizeLabels } from "../data/radius-option";
import type { EventDetailForAdminResponse } from "../types/event.types";
import { formatEventDate, formatEventTime } from "../utils/event.utils";
import StaticMap from "./StaticMap";

type EventDetailsViewProps = {
  event: EventDetailForAdminResponse;
};

type DetailMetricProps = {
  icon: ReactNode;
  label: string;
  value: ReactNode;
  detail?: ReactNode;
};

const isNonEmptyString = (value: string | undefined | null): value is string => Boolean(value);

const getVenueLabel = (radius?: number) => {
  if (!radius) return "Not specified";

  return VenueSizeLabels[String(radius) as VenueSize] ?? `${radius.toLocaleString()} ft`;
};

const DetailMetric = ({ icon, label, value, detail }: DetailMetricProps) => (
  <div className="flex min-w-0 gap-3 px-4 py-3 sm:px-5">
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-300">
      {icon}
    </div>
    <div className="min-w-0">
      <p className="text-xs font-medium text-slate-500 dark:text-slate-400">{label}</p>
      <p className="mt-1 truncate text-sm font-semibold text-slate-900 dark:text-white">{value}</p>
      {detail ? <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{detail}</p> : null}
    </div>
  </div>
);

const SectionHeading = ({ icon, children }: { icon: ReactNode; children: ReactNode }) => (
  <div className="mb-4 flex items-center gap-3">
    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-300">
      {icon}
    </div>
    <h2 className="text-base font-bold text-slate-900 dark:text-white">{children}</h2>
  </div>
);

export default function EventDetailsView({ event }: EventDetailsViewProps) {
  const categoryNames = [event.category_name].filter(isNonEmptyString);
  const hasCoordinates = Number.isFinite(event.latitude) && Number.isFinite(event.longitude);
  const mapUrl = hasCoordinates
    ? `https://www.google.com/maps/search/?api=1&query=${event.latitude},${event.longitude}`
    : undefined;
  const timezone = event.time_zone || "Local time";
  const checkInTime = event.check_in_time
    ? formatEventTime(event.check_in_time, event.time_zone)
    : "Not specified";

  return (
    <main className="min-h-full bg-slate-50 px-4 py-6 dark:bg-slate-950 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <Card className="overflow-hidden border-slate-200 shadow-sm dark:border-slate-800">
          {event.image_url ? (
            <img
              src={event.image_url}
              alt={`Event cover for ${event.title}`}
              className="h-52 w-full object-cover sm:h-72"
            />
          ) : (
            <div className="flex h-52 items-center justify-center bg-[radial-gradient(circle_at_top_left,_#d1fae5,_#ecfdf5_45%,_#f8fafc)] text-emerald-700 dark:bg-[radial-gradient(circle_at_top_left,_#064e3b,_#0f172a_60%)] dark:text-emerald-200 sm:h-72">
              <div className="flex flex-col items-center gap-3 text-center">
                <ImageOff className="h-9 w-9" aria-hidden="true" />
                <span className="text-sm font-semibold">No event cover image</span>
              </div>
            </div>
          )}
        </Card>

        <Card className="mt-5 border-slate-200 shadow-sm dark:border-slate-800">
          <CardContent className="grid divide-y divide-slate-200 p-0 dark:divide-slate-800 sm:grid-cols-2 sm:divide-x sm:divide-y-0 xl:grid-cols-6">
            <DetailMetric
              icon={<CalendarDays className="h-5 w-5" aria-hidden="true" />}
              label="Date"
              value={formatEventDate(event.event_date || "", event.time_zone)}
            />
            <DetailMetric
              icon={<Clock3 className="h-5 w-5" aria-hidden="true" />}
              label="Time"
              value={`${formatEventTime(event.start_time || "", event.time_zone)} – ${formatEventTime(
                event.end_time || "",
                event.time_zone,
              )}`}
              detail={timezone}
            />
            <DetailMetric
              icon={<MapPin className="h-5 w-5" aria-hidden="true" />}
              label="Location"
              value={event.location || "Not specified"}
            />
            <DetailMetric
              icon={<Tag className="h-5 w-5" aria-hidden="true" />}
              label="Category"
              value={categoryNames[0] || "Not specified"}
              detail={undefined}
            />
            <DetailMetric
              icon={<ScanLine className="h-5 w-5" aria-hidden="true" />}
              label="Check-in radius"
              value={getVenueLabel(event.radius)}
              detail="Allowed check-in area"
            />
            <DetailMetric
              icon={<Gift className="h-5 w-5" aria-hidden="true" />}
              label="Points / Rewards"
              value={event.points !== null ? `${event.points.toLocaleString()} points` : "Not specified"}
              detail="Participation reward"
            />
          </CardContent>
        </Card>

        <div className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
          <div className="grid gap-5">
            <Card className="border-slate-200 shadow-sm dark:border-slate-800">
              <CardContent className="p-5 sm:p-6">
                <SectionHeading icon={<FileText className="h-5 w-5" aria-hidden="true" />}>
                  About this event
                </SectionHeading>
                <p className="whitespace-pre-line text-sm leading-7 text-slate-600 dark:text-slate-300">
                  {event.description || "No event description has been provided."}
                </p>
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm dark:border-slate-800">
              <CardContent className="p-5 sm:p-6">
                <SectionHeading icon={<MapPin className="h-5 w-5" aria-hidden="true" />}>
                  Location
                </SectionHeading>
                <div className="grid gap-5 md:grid-cols-[minmax(0,0.7fr)_minmax(0,1.3fr)]">
                  <div className="space-y-4">
                    <p className="text-sm leading-6 text-slate-600 dark:text-slate-300">
                      {event.location || "Location details have not been provided."}
                    </p>
                    <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-3 text-sm text-emerald-900 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-100">
                      <div className="flex items-center gap-2 font-semibold">
                        <ScanLine className="h-4 w-4" aria-hidden="true" />
                        Check-in radius: {getVenueLabel(event.radius)}
                      </div>
                      <p className="mt-1 text-xs leading-5 text-emerald-800/80 dark:text-emerald-200/80">
                        The shaded circle on the map shows the allowed check-in area.
                      </p>
                    </div>
                    {mapUrl ? (
                      <Button
                        asChild
                        type="button"
                        variant="outline"
                        className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-800 dark:text-emerald-300 dark:hover:bg-emerald-950/40"
                      >
                        <a href={mapUrl} target="_blank" rel="noreferrer">
                          Open in Google Maps
                          <ExternalLink aria-hidden="true" />
                        </a>
                      </Button>
                    ) : null}
                  </div>
                  {hasCoordinates ? (
                    <StaticMap
                      latitude={event.latitude}
                      longitude={event.longitude}
                      location={event.location || undefined}
                      radius={event.radius}
                      height="220px"
                      className="mb-0"
                    />
                  ) : (
                    <div className="flex min-h-52 items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400">
                      Map coordinates are not available for this event.
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-5">
            <Card className="border-slate-200 shadow-sm dark:border-slate-800">
              <CardContent className="p-5 sm:p-6">
                <SectionHeading icon={<Clock3 className="h-5 w-5" aria-hidden="true" />}>
                  Schedule & details
                </SectionHeading>
                <dl className="grid gap-3 text-sm">
                  {[
                    ["Start time", formatEventTime(event.start_time || "", event.time_zone)],
                    ["End time", formatEventTime(event.end_time || "", event.time_zone)],
                    ["Timezone", timezone],
                    ["Check-in time", checkInTime],
                  ].map(([label, value]) => (
                    <div key={label} className="flex items-start justify-between gap-4">
                      <dt className="text-slate-500 dark:text-slate-400">{label}</dt>
                      <dd className="text-right font-semibold text-slate-900 dark:text-white">{value}</dd>
                    </div>
                  ))}
                </dl>
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm dark:border-slate-800">
              <CardContent className="p-5 sm:p-6">
                <SectionHeading icon={<Gift className="h-5 w-5" aria-hidden="true" />}>
                  Rewards & attendance
                </SectionHeading>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="rounded-xl bg-slate-50 p-4 dark:bg-slate-900">
                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Points</p>
                    <p className="mt-1 text-2xl font-bold text-slate-950 dark:text-white">
                      {event.points !== null ? event.points.toLocaleString() : "—"}
                    </p>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      Awarded for participation
                    </p>
                  </div>
                  <div className="rounded-xl bg-slate-50 p-4 dark:bg-slate-900">
                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                      Check-in radius
                    </p>
                    <p className="mt-1 text-sm font-bold text-slate-950 dark:text-white">
                      {getVenueLabel(event.radius)}
                    </p>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      Attendees must be within this range
                    </p>
                  </div>
                </div>
                <div className="mt-5 flex items-start gap-3 border-t border-slate-200 pt-4 dark:border-slate-800">
                  <CheckCircle2
                    className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-300"
                    aria-hidden="true"
                  />
                  <div>
                    <p className="text-sm font-semibold text-slate-900 dark:text-white">
                      Guest attendance verification
                    </p>
                    <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                      {event.guest_attendance_verification || "Not required"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </main>
  );
}